<script setup lang="ts">
import type { PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
    (e: 'change-website', value: number): void;
    (e: 'change-locale', value: string): void;
}>();

const props = withDefaults(defineProps<{
    modelValue?: number;
    module?: string;
    disabled?: boolean;
    website?: boolean;
    label?: string;
    invalidFeedback?: string;
    state?: boolean | null;
    required?: boolean;
    websiteId?: number;
    locale?: string;
    excludeModules?: string[];
}>(), {
    modelValue: null,
    module: '',
    disabled: false,
    website: true,
    label: '[[[Publikuj na stronie]]]',
    invalidFeedback: '',
    state: null,
    required: false,
    websiteId: null,
    locale: null,
    excludeModules: () => [] as string[]
});

const layoutContext = inject<ComputedRef<{ websiteId: number; locale: string } | null>>('layoutPageSelectorContext', null);

const store = useWebsitesStore();
const { $log } = useLogging();

const modelWebsiteData = ref<number>(null);
const modelLocaleData = ref('');
const reloadKey = ref(1);
const pages = ref<PageListItemModel[]>([]);
const pagesReady = ref(false);

const current = computed<CurrentWebsite>(() =>
{
    return store.current;
});

const modelWebsite = computed({
    get: () => modelWebsiteData.value,
    set: (value: number) => { modelWebsiteData.value = value; }
});

const modelLocale = computed({
    get: () => modelLocaleData.value,
    set: (value: string) => { modelLocaleData.value = value; }
});

function normalizePageId(value: number | string | null | undefined): number | null
{
    if (value == null || value === '')
        return null;

    const parsed = Number(value);

    return Number.isNaN(parsed) ? null : parsed;
}

const pageId = computed({
    get(): number | null
    {
        return normalizePageId(props.modelValue);
    },
    set(value: number | null)
    {
        triggerModel(value);
    }
});

const page = computed<PageListItemModel | undefined>(() =>
{
    if (!pageId.value)
    {
        return undefined;
    }

    return pages.value.find(item => item.id === pageId.value);
});

const pageOptions = computed<Option[]>(() =>
{
    const excludedModules = props.excludeModules ?? [];

    const excludedRootIds = new Set<number>();
    const excludedDescendantIds = new Set<number>();

    pages.value.forEach((page) =>
    {
        if (page.module && excludedModules.includes(page.module))
        {
            excludedRootIds.add(page.id);
        }
    });

    function collectDescendants(parentId: number): void
    {
        pages.value.forEach((page) =>
        {
            if (page.parentId === parentId && !excludedDescendantIds.has(page.id))
            {
                excludedDescendantIds.add(page.id);
                collectDescendants(page.id);
            }
        });
    }

    excludedRootIds.forEach((id) =>
    {
        collectDescendants(id);
    });

    return pages.value
        .filter((page) =>
        {
            if (excludedDescendantIds.has(page.id))
            {
                return false;
            }

            return true;
        })
        .map((page) =>
        {
            const isDisabledByModule = props.module ? page.module !== props.module : false;
            const isDisabledById = page.id === 0;

            return {
                value: page.id.toString(),
                text: page.option,
                disabled: isDisabledByModule || isDisabledById
            };
        });
});

const selectPageId = computed({
    get(): number | null
    {
        const id = pageId.value;

        if (id == null || !pagesReady.value)
            return null;

        if (!pageOptions.value.some(p => Number(p.value) == id))
            return null;

        return id;
    },
    set(value: number | null)
    {
        pageId.value = value;
    }
});

function triggerModel(value: number | null): void
{
    if (value == null)
    {
        emit('update:modelValue', null);

        return;
    }

    if (typeof props.modelValue === 'string')
        emit('update:modelValue', value.toString() as any);
    else
        emit('update:modelValue', value);
}

function effectiveWebsiteId(): number
{
    if (props.website)
        return modelWebsite.value ?? props.websiteId ?? current.value.website;

    return layoutContext?.value?.websiteId ?? props.websiteId ?? current.value.website;
}

function effectiveLocale(): string
{
    if (props.website)
        return modelLocale.value || props.locale || current.value.locale;

    return layoutContext?.value?.locale ?? props.locale ?? current.value.locale;
}

function hasContextValue(value: unknown): boolean
{
    return value != null && value !== '' && value !== 0;
}

async function onWebsiteChanged(value: number): Promise<number>
{
    pageId.value = null;
    modelWebsite.value = value;
    emit('change-website', value);
    await loadPages();

    return value;
}

async function onLocaleChanged(value: string): Promise<string>
{
    pageId.value = null;
    modelLocale.value = value;
    emit('change-locale', value);
    await loadPages();

    return value;
}

async function loadPages(): Promise<void>
{
    const websiteId = effectiveWebsiteId();
    const locale = effectiveLocale();

    pagesReady.value = false;

    modelWebsite.value = websiteId;
    modelLocale.value = locale;

    if (!websiteId || !locale)
        return;

    try
    {
        pages.value = await PagesService.getPagesByModule(modelWebsite.value, modelLocale.value, props.module);
        reloadKey.value++;
    }
    catch (ex)
    {
        pages.value = [];
        $log.debug(ex);
    }
    finally
    {
        pagesReady.value = true;
    }
}

watch(() => props.website
    ? [modelWebsite.value, modelLocale.value, props.websiteId, props.locale]
    : [props.websiteId, props.locale, layoutContext?.value?.websiteId, layoutContext?.value?.locale], async (values, oldValues) =>
{
    const changed = values.some((value, index) => value !== oldValues?.[index]);

    if (changed && oldValues?.some(value => hasContextValue(value)))
    {
        pageId.value = null;
    }

    await loadPages();
});

onBeforeMount(async (): Promise<void> =>
{
    if (effectiveWebsiteId() && effectiveLocale())
    {
        await loadPages();

        return;
    }

    await wait(() => current.value.website);
    await loadPages();
});

defineExpose({
    page
});
</script>

<template>
    <IdeoFormGroup :label="$t(label)" :invalid-feedback="invalidFeedback" :state="state" :required="required">
        <ElearningWebsiteSelector v-if="website" :disabled="disabled" :website-id="websiteId" :locale="locale" @change-website="onWebsiteChanged" @change-locale="onLocaleChanged" />
        <div class="input-group mt-2">
            <IdeoFormSelect v-if="pagesReady" :key="reloadKey" v-model="selectPageId" :options="pageOptions" :disabled="disabled" data-cy="page">
                <template #first>
                    <option :value="null">
                        {{ $t('[[[Wybierz stronę]]]') }}
                    </option>
                </template>
            </IdeoFormSelect>
        </div>
    </IdeoFormGroup>
</template>
